import { PATHS } from '@ctcells/core'
import { Navigate, type RouteObject } from 'react-router'

import { AccountContainer } from '../screens/account/AccountContainer.tsx'
import { AdminContainer } from '../screens/admin/AdminContainer.tsx'
import { adminStopsPath } from '../screens/admin/adminTabs.ts'
import { WEBHOOK_DETAIL_ROUTE } from '../screens/admin/webhookPaths.ts'
import { LogsContainer } from '../screens/logs/LogsContainer.tsx'
import { MembersSplitRoute, WebhooksSplitRoute } from './consoleAdminViews.tsx'
import { ModulesSplitRoute } from './consoleModuleViews.tsx'
import { RecipeNewModalRoute, RecipeNewStandardModalRoute } from './consoleResourceModalViews.tsx'
import {
  RecipesSplitRoute,
  SampleNewModalRoute,
  SamplesSplitRoute,
} from './consoleResourceViews.tsx'
import { LoginRoute } from './LoginRoute.tsx'
import { TemplatesSplitRoute } from './consoleTemplateViews.tsx'

/**
 * 콘솔 「리소스」·「시스템」 그룹 + 로그인·내 계정 라우트 조각.
 *
 * 🔴 **데이터와 권한은 컨테이너가 넣는다.** 라우트는 컨테이너를 걸 뿐이고, 화면 컴포넌트는
 *    한 줄도 바뀌지 않았다. 목/실물이 갈리는 지점은 각 컨테이너 안의 `dataSourceFor()` 하나다.
 *
 * 🔴 **이 표의 모든 화면이 컨테이너를 거친다.** 화면을 라우트에 직접 걸지 마라 —
 *    그러면 `dataSourceFor()` 를 건너뛰어 **`live` 설정과 무관하게 목이 뜬다.**
 *    2026-09-18 에 모듈 3화면이 실제로 그 상태였고, eric 이 서버가 준 적 없는 IP·응답시간·
 *    버전을 화면에서 보고 잡아냈다. 권한(`access`)까지 `DEMO_ACCESS` 로 박혀 있었다.
 *
 * | 화면                | 상태                                                                       |
 * | ------------------- | -------------------------------------------------------------------------- |
 * | 시료(목록·상세·등록) | ✅ 실물 연결                                                                |
 * | 로그                | ✅ 실물 연결                                                                |
 * | 관리 · 사용자 상세  | ✅ 실물 연결                                                                |
 * | 레시피 · 랩웨어     | 🔸 컨테이너는 같은 모양으로 있고 기본이 `demo` 다(사유는 각 컨테이너 주석)   |
 * | 모듈 등록·상세·보정 | ✅ 실물 연결(2026-09-18). ⚠ 다만 **표의 열 7개 중 4개는 계약에 값이 없어** `—` |
 * |                     | 다(IP · 응답 확인 · 최근 진단 · 버전). 열은 남긴다 — 사유는 `moduleRows.ts` 표. |
 * |                     | 「보정」만 성격이 다르다: 경로는 있고 실물이 0건이라 값이 차면 저절로 뜬다     |
 * | 내 계정             | ✅ 실물 연결(세션 표). ⚠ 내 `user_id` 의 출처는 **로그인 응답의 `user`** 하나다 |
 * |                     | — `/auth/me` 는 생겼으나(2026-09-26) 아직 안 탔다. 사정은 `AccountContainer` 주석 |
 */
export const consoleResourceRoutes: RouteObject[] = [
  /*
   * 🔴 **무리 탭 값은 «라우트»가 넘긴다.** 이 표는 시료 관점 콘솔(`apps/console`)의 것이라
   *    시료·레시피·랩웨어를 「준비」로, 모듈·로그를 「관리」로 묶어 보여준다.
   * ⚠ **안 넘기면 탭 줄이 사라진다**(`useGroupTabLayout` 주석). 장비 관점 콘솔
   *   (`apps/console-equipment`)의 표가 바로 그렇게 쓴다 — 거기선 여섯이 사이드바에 각자 있다.
   */
  /* 목록 + 오른쪽 상세 판 — 두 주소가 같은 화면이다. `:id` 가 있으면 판이 열린다(eric 2026-09-22) */
  { path: PATHS.samples, element: <SamplesSplitRoute /> },
  /* 추가는 목록 위의 창이다(eric 2026-09-30). 페이지로 되돌리려면 `SampleNewContainer` */
  { path: PATHS.sampleNew, element: <SampleNewModalRoute /> },
  { path: '/samples/:sampleId', element: <SamplesSplitRoute /> },

  { path: PATHS.recipes, element: <RecipesSplitRoute /> },
  /* 추가는 목록 위의 창이다(eric 2026-09-30). 페이지로 되돌리려면 `RecipeNewContainer` */
  { path: PATHS.recipeNew, element: <RecipeNewModalRoute /> },
  /* 표준 레시피(새 형식)에서 — 「＋ 레시피 추가」 메뉴(2026-10-06) */
  { path: PATHS.recipeNewStandard, element: <RecipeNewStandardModalRoute /> },
  { path: '/recipes/:recipeId', element: <RecipesSplitRoute /> },

  /*
   * 🔴 **옛 랩웨어 카탈로그 주소 → 작업 준비 › 템플릿 목록**(2026-10-11 → 2026-10-26). 서버 v0.9.4 가 `/labware-definitions` 를
   *    걷었다(core `1cd3f92a`). 10-11 엔 템플릿의 `labware` 종류로 보냈는데, 10-26 에 템플릿이 «레시피» 하나만 남아(eric 「응.. 다 빼줘」)
   *    목록으로 바로 보낸다 — 두 번 튕기지 않게.
   * ⚠ `replace` 다 — 뒤로 가기가 이 리다이렉트를 다시 타지 않게.
   */
  { path: PATHS.labware, element: <Navigate replace to={PATHS.templates} /> },
  { path: PATHS.labwareNew, element: <Navigate replace to={PATHS.templates} /> },
  { path: `${PATHS.labware}/:labwareId`, element: <Navigate replace to={PATHS.templates} /> },

  /*
   * 템플릿 — «레시피» 하나뿐(eric 2026-10-26). 옛 종류 주소(`/labware` · `/reagent` · `/work` · `/deck` · `…/new`)는
   * `TemplatesSplitRoute` 가 목록으로 보낸다(경로는 그대로 받아야 보낼 수 있다)
   */
  { path: PATHS.templates, element: <TemplatesSplitRoute /> },
  { path: `${PATHS.templates}/:kind`, element: <TemplatesSplitRoute /> },
  { path: `${PATHS.templates}/:kind/:itemId`, element: <TemplatesSplitRoute /> },

  /*
   * 모듈도 목록 + 오른쪽 상세 판이다(eric 2026-09-22).
   * 🔴 보정 상세(`/modules/:moduleId/calibrations/:calibrationId`) 라우트는 없다 — 들어가는 길(모듈 상세 「보정」 탭)을 걷었다
   *    (eric 2026-10-22 「보정과 시스템 로그 탭은 제거해줘..」). 주소 직접 진입만 남는 죽은 경로를 두지 않는다(팀장 결정).
   *    화면 파일은 장비 관점 콘솔 라우트가 써서 남는다(`apps/console-equipment/src/routes.tsx`)
   */
  { path: PATHS.modules, element: <ModulesSplitRoute /> },
  { path: '/modules/:moduleId', element: <ModulesSplitRoute /> },

  { path: PATHS.logs, element: <LogsContainer adminTab="logs" /> },

  /*
   * 🔴 **관리 탭 4종은 각자 주소를 갖는다**(2026-09-18 eric 지시로 모듈·로그가 관리 안으로 들어왔다).
   *    탭 목록의 유일한 출처는 `screens/admin/adminTabs.ts` 이고, 여기 경로가 그것과 짝이다.
   *    사용자(`/admin`) · 설정(`/admin/general`) · 모듈(`/modules`) · 로그(`/logs`).
   * ⚠ **상세 3종은 탭 밖이다** — 탭 줄 없이 전체 화면으로 뜨고, 「←」로 돌아가면 제 탭이 열린다.
   */
  /* 사용자도 목록 + 오른쪽 상세 판이다(eric 2026-09-22). 설정 탭은 종전 그대로 */
  { path: PATHS.admin, element: <MembersSplitRoute /> },
  { path: PATHS.adminGeneral, element: <AdminContainer tab="general" /> },
  /* 웹훅 — 목록 + 오른쪽 상세 판(eric 2026-10-29 · `WebhooksSplitRoute`) */
  { path: PATHS.adminWebhooks, element: <WebhooksSplitRoute /> },
  { path: WEBHOOK_DETAIL_ROUTE, element: <WebhooksSplitRoute /> },
  /*
   * 🔴 **「걸린 것」 주소 → 설정 탭 그 절로 보낸다**(2026-10-11 · 종전 「자원 점유」 절 자리).
   *    화면은 `GeneralSettings` 안의 `StopsContainer` 하나뿐이고 여기는 길만 남긴다.
   * ⚠ **이 두 줄을 지우지 마라.** 작업 상세의 복구 대기 구역이 `PATHS.adminStops` 로 걸어 두었다 —
   *   핸드오프에서 선 배치를 푸는 길이 화면에 없어 사람이 반나절을 썼던 자리다. 옛 주소
   *   `PATHS.adminResourceLocks` 도 즐겨찾기·옛 링크를 위해 같은 절로 보낸다.
   * ⚠ `replace` 다 — 뒤로 가기가 이 리다이렉트를 다시 타면 되돌아갈 길이 없다.
   */
  { path: PATHS.adminStops, element: <Navigate replace to={adminStopsPath} /> },
  { path: PATHS.adminResourceLocks, element: <Navigate replace to={adminStopsPath} /> },
  { path: '/admin/members/:userId', element: <MembersSplitRoute /> },

  { path: PATHS.account, element: <AccountContainer /> },
]

/**
 * 로그인. `AuthLayout` 아래에 꽂힌다.
 * 🔴 화면이 아니라 **컨테이너**를 건다 — `POST /auth/login` 을 부르는 자리는 `LoginRoute` 다.
 */
export const loginScreen = <LoginRoute />
